<template>
  <div>
    <!-- table -->
    <vue-good-table
      ref="googleAdsTable"
      :columns="columns"
      :rows="googleAds"
      :rtl="direction"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'status', type: 'asc'}
      }"
      :pagination-options="{
        enabled: false,
        perPage:pageLength
      }"
      style-class="vgt-table striped"
    >
      <template slot="column-filter">
        <span style="display:none;" />
      </template>
      <!-- Slot: Table Column -->
      <template
        slot="table-column"
        slot-scope="props"
      >
        <span
          v-if="props.column.label ==='Status'"
          class="text-nowrap"
        >
          {{ $t('campaigns.status') }}
        </span>
        <span
          v-else-if="props.column.label ==='Name'"
          class="text-nowrap"
        >
          {{ $t('campaigns.title') }}
        </span>
        <span
          v-else-if="props.column.label ==='Code'"
          class="text-nowrap"
        >
          {{ 'Ad Code' }}
        </span>
        <span
          v-else-if="props.column.label ==='Action'"
          class="text-nowrap"
        >
          {{ $t('Action') }}
        </span>
        <span v-else>
          {{ props.column.label }}
        </span>
      </template>

      <!-- Slot: Table Row -->
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Column: Status -->
        <span
          v-if="props.column.field === 'title'"
          class="text-nowrap"
        >
          <a
            :href="`/users/${props.row.user_id}/offerwall/${props.row._id}/stats`"
            class="text-primary font-weight-bold"
            @click.prevent="$router.push(`/users/${props.row.user_id}/offerwall/${props.row._id}/stats`)"
          >
            {{ props.row.title }}
          </a>
          <b-badge :variant="status[props.row.status]">
            {{ $t(`selects.statuses.${props.row.status}`) }}
          </b-badge>
          <br>
          <small v-if="props.row.config && props.row.config.gpt && props.row.config.gpt.siteDomain">{{ props.row.config.gpt.siteDomain }}</small>
        </span>
        <span
          v-else-if="props.column.field === 'code'"
          class="text-nowrap"
        >
          <b-button
            v-clipboard:copy="googleAdCode(props.row._id)"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
            v-b-tooltip.hover.top="$t('widgets.copybutton')"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-primary"
            class="btn-sm btn-icon"
          >
            <feather-icon icon="CopyIcon" />
          </b-button>
        </span>
        <span
          v-else-if="props.column.field === 'status'"
          class="text-nowrap"
        >
          <b-form-checkbox
            :checked="props.row.status === 'active' ? true : false"
            name="check-button"
            switch
            size="lg"
            @change="changeStatus(props.row._id)"
          />
        </span>
        <span v-else-if="props.column.field === 'action'">
          <span class="d-flex justify-content-center">
            <b-button
              v-b-tooltip.hover.top="$t('form.change')"
              :to="'/users/'+ props.row.user_id + '/offerwall/'+ props.row._id + '/edit/'"
              variant="flat"
              class="btn-action"
            >
              <feather-icon
                icon="EditIcon"
                class="text-primary"
                size="16"
              />
            </b-button>

            <b-button
              v-if="authUserData.role.includes('admin')"
              v-b-tooltip.hover.top="$t('form.delete')"
              variant="flat"
              class="btn-action"
              @click="deleteGoogleAd(props.row._id, props.row.title)"
            >
              <feather-icon
                icon="Trash2Icon"
                class="mr-50 text-danger"
                size="16"
              />
            </b-button>
          </span>
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              {{ $t('message.pagelength') }}
            </span>
            <b-form-select
              v-model="localPageLength"
              :options="['5','10','20','40']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap">  {{ $t('message.of') }} {{ props.total }} {{ $t('message.pageText2') }} </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="localPageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import {
  BBadge,
  BPagination,
  BFormSelect,
  BFormCheckbox,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    VueGoodTable,
    BBadge,
    BPagination,
    BFormSelect,
    BFormCheckbox,
    BButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  props: {
    googleAds: {
      type: Array,
      require: true,
      default: () => [],
    },
    authUserData: {
      type: Object,
      require: true,
      default: () => {},
    },
    account: {
      type: Object,
      require: true,
      default: () => {},
    },
    pageLength: {
      type: Number,
      require: true,
      default: 10,
    },
  },
  data() {
    return {
      dir: false,
      columns: [
        {
          label: 'Name',
          field: 'title',
        },
        {
          label: 'Status',
          field: 'status',
          type: 'boolen',
        },
        {
          label: '_id',
          field: '_id',
          hidden: true,
        },
        {
          label: 'user_id',
          field: 'user_id',
          hidden: true,
        },
        {
          label: 'Code',
          field: 'code',
          sortable: false,
          width: '140px',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Action',
          field: 'action',
          thClass: 'text-center',
          tdClass: 'text-center',
          width: '5%',
          sortable: false,
        },
      ],
      status: {
        active: 'light-success',
        inactive: 'light-danger',
        is_moderate: 'light-warning',
      },
      localPageLength: this.pageLength,
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  watch: {
    localPageLength(newVal) {
      this.$emit('update:pageLength', parseInt(newVal, 10))
    },
  },
  methods: {
    googleAdCode(adId) {
      return `<script src="//phoenix-widget.com/static/js/phx-modal.js?ad_id=${adId}"><${''}/script>`
    },
    onCopy() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('widgets.copybutton') || 'Code copied',
          icon: 'BellIcon',
          variant: 'success',
        },
      })
    },
    onError() {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('form.error') || 'Failed to copy code!',
          icon: 'AlertCircleIcon',
          variant: 'danger',
        },
      })
    },
    async changeStatus(id) {
      try {
        const responseData = await useJwt.getGoogleWidget(id)
        const googleWidget = responseData.data.googlewidget || {}
        googleWidget.status = googleWidget.status === 'inactive' ? 'active' : 'inactive'

        if (googleWidget.status === 'active') {
          this.$swal({
            icon: 'success',
            title: 'Activation!',
            text: 'Successfully activated.',
            customClass: {
              confirmButton: 'btn btn-success',
            },
          })
          await useJwt.updateGoogleWidget(id, { googlewidget: googleWidget })
          this.$emit('fetchAccountGoogleAds', this.account._id)
        } else {
          this.$swal({
            title: 'Confirmation',
            text: 'Will be inactive',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ok',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
          }).then(async result => {
            if (result.value) {
              this.$swal({
                icon: 'success',
                title: 'Deactivation!',
                text: 'Successfully deactivated',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
              await useJwt.updateGoogleWidget(id, { googlewidget: googleWidget })
              this.$emit('fetchAccountGoogleAds', this.account._id)
            } else if (result.dismiss === 'cancel') {
              this.$swal({
                title: 'Cancelled',
                text: 'Status remain active',
                icon: 'error',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
            }
          })
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('form.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    deleteGoogleAd(id, name) {
      this.$swal({
        title: `${this.$t('swal.deletetitle')} ${name}?`,
        text: this.$t('google_ads.deletemessage'),
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: this.$t('form.cancel'),
        confirmButtonText: this.$t('form.delete'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          try {
            const deleted = await useJwt.deleteGoogleWidget(id)
            if (deleted.status === 200 || deleted.status === 204) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('swal.successfullydeleted'),
                  icon: 'BellIcon',
                  variant: 'success',
                },
              })
              this.$emit('fetchAccountGoogleAds', this.account._id)
            }
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('form.error'),
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            })
          }
        }
      })
    },
  },
}
</script>
<style>
@media (min-width: 1440px) {
  .vgt-responsive {
    overflow-x: visible !important;
  }
}
</style>
